import { memo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { type ApolloCache, useMutation } from "util/graphql";
import { useForm } from "common/core/form";
import { TextInput } from "common/core/form/text";
import { Label } from "common/core/form/layout";
import { useId } from "util/html";
import {
  isAriaInvalid,
  FormattedFieldError,
  FieldErrorMessage,
  defaultRequiredMessage,
} from "common/core/form/error";
import Button from "common/core/button";
import { segmentTrack } from "util/segment";

import Styles from "./proof_otp.module.scss";
import SendProofOTPMutation, { type SendProofOTP } from "./send_proof_otp_mutation.graphql";

type MeetingFormProps = {
  meetingId: string;
  onSuccess: (meetingId: string) => void;
  onCacheUpdate?: (
    cacheProxy: ApolloCache<unknown>,
    response: { data?: SendProofOTP | null },
  ) => void;
};

type FormValues = {
  otp: string;
};
type FormProps = {
  onSend: (values: FormValues) => Promise<unknown>;
};

const PROOF_OTP_TITLE_COPY = (
  <FormattedMessage
    id="5e8366cd-8a62-453a-8ae8-3353fbb28cf7"
    defaultMessage="Authenticate your Proof Certificate"
  />
);

function getErrorCodeMessage(serverError: string | undefined) {
  switch (serverError) {
    case "The otp is invalid.":
      return (
        <FormattedMessage
          id="b2c5226b-5091-4ad1-87ed-c86f7c066690"
          defaultMessage="The code you entered is either invalid or expired. Please try again."
        />
      );
    case "unprocessable_entity":
      return (
        <FormattedMessage
          id="c0001090-642c-4d08-af87-26b73859876d"
          defaultMessage="An unexpected error occurred. Please try again later."
        />
      );
    default:
      return (
        <FormattedMessage
          id="d3aefe66-319e-4874-a83e-eb457eacdb59"
          defaultMessage="Something went wrong. Please contact support."
        />
      );
  }
}

function OTPCodeForm({ onSend }: FormProps) {
  const intl = useIntl();
  const ProofOtpID = useId();
  const [errorMessage, setErrorMessage] = useState<ReturnType<typeof getErrorCodeMessage> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register, formState, handleSubmit } = useForm<FormValues>();
  const handleSend = (values: FormValues) => {
    setErrorMessage(null);
    setIsLoading(true);
    return onSend(values)
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(getErrorCodeMessage(error?.message));
        segmentTrack("SEND_PROOF_OTP_ERROR", error);
      });
  };
  return (
    <form onSubmit={handleSubmit(handleSend)}>
      <div className={Styles.otpInputContainer}>
        <Label htmlFor={ProofOtpID}>
          <FormattedMessage
            id="f5a9bc67-1642-441c-a416-648ecd5abcf0"
            defaultMessage="Enter 6-digit verification code"
          />
        </Label>
        <TextInput
          id={ProofOtpID}
          autoFocus
          aria-invalid={Boolean(errorMessage) || isAriaInvalid(formState.errors.otp)}
          {...register("otp", { required: defaultRequiredMessage(intl) })}
        />
        {errorMessage ? (
          <FieldErrorMessage inputName="otp" message={errorMessage} />
        ) : (
          <FormattedFieldError inputName="otp" error={formState.errors.otp} />
        )}
      </div>
      <div className={Styles.buttonContainer}>
        <Button
          className={Styles.formButton}
          type="submit"
          buttonColor="action"
          variant="primary"
          isLoading={isLoading}
        >
          <FormattedMessage
            id="44bf0fed-a878-40c9-98c4-d0349f84adf3"
            defaultMessage="Confirm code and complete"
          />
        </Button>
      </div>
    </form>
  );
}

function MeetingOTPCodeForm({ meetingId, onSuccess, onCacheUpdate }: MeetingFormProps) {
  const sendProofOtpMutateFn = useMutation(SendProofOTPMutation);
  return (
    <OTPCodeForm
      onSend={({ otp }) => {
        return sendProofOtpMutateFn({
          variables: { input: { meetingId, otp } },
          update: onCacheUpdate,
        }).then(() => onSuccess(meetingId));
      }}
    />
  );
}
const MemoizedMeetingOTPCodeForm = memo(MeetingOTPCodeForm);

export { PROOF_OTP_TITLE_COPY, MemoizedMeetingOTPCodeForm as ProofMeetingOTPCodeForm };
